import { useSuspenseQuery } from '@apollo/client';
import React, { Fragment, useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { match } from 'ts-pattern';

import { reviewerHasuraContext } from '@eluve/apollo-client';
import { TranscriptTimestampDivider } from '@eluve/blocks';
import { Box, HStack, NewButton, P, VStack } from '@eluve/components';
import { DynamicArtifactRenderer } from '@eluve/smart-blocks';

import { AppointmentAudio } from '../AppointmentAudio';
import { getReviewerAppointmentDetails } from '../operations';
import { useAppointmentParams } from '../useAppointmentParams';

import { GoldenSummaryContextProvider } from './GoldenSummaryContextProvider';
import { GoldenSummaryEditor } from './GoldenSummaryEditor';
import { GoldenSummarySelect } from './GoldenSummarySelect';
import { useSummary } from './useSummary';

type TAB_OPTIONS = 'TRANSCRIPT' | 'CORRECTED_TRANSCRIPT' | 'SUMMARY';

type TranscriptSegment = {
  transcript: string;
  recordingStartedAt: string;
};

export const GoldenSummaryContent: React.FC = () => {
  const { tenantId, appointmentId } = useAppointmentParams();
  const [activeTab, setActiveTab] = useState<TAB_OPTIONS>('TRANSCRIPT');
  const [activeNoteId, setActiveNoteId] = useState<string | null>(null);
  const [correctedTranscription, setCorrectedTranscription] = useState('');
  const [transcriptSegments, setTranscriptSegments] = useState<
    TranscriptSegment[]
  >([]);
  const { llmSummary, originalContent } = useSummary({
    appointmentId,
  });

  const { data } = useSuspenseQuery(getReviewerAppointmentDetails, {
    variables: {
      tenantId,
      appointmentId,
    },
    context: reviewerHasuraContext,
  });

  useEffect(() => {
    if (data?.appointmentsByPk?.segments) {
      const sortedSegments = data.appointmentsByPk.segments
        .slice()
        .sort((a, b) => {
          const aTime = new Date(a.recordingStartedAt ?? 0).getTime();
          const bTime = new Date(b.recordingStartedAt ?? 0).getTime();
          return aTime - bTime;
        })
        .map((s) => ({
          transcript: s.transcript || '',
          recordingStartedAt: s.recordingStartedAt || new Date().toISOString(),
        }));

      setTranscriptSegments(sortedSegments);

      const hasAnyCorrections = data.appointmentsByPk.segments.some((segment) =>
        segment.utterances?.some((utterance) => utterance.hasHumanUpdatedText),
      );

      if (hasAnyCorrections) {
        setCorrectedTranscription(
          data.appointmentsByPk.segments
            .flatMap((segment) =>
              segment.utterances
                .map((u) => u.speaker + ': ' + u.text)
                .join('\n'),
            )
            .join('\n\n'),
        );
      }
    }
  }, [data]);

  const handleNoteChange = (value: string) => {
    setActiveNoteId(value);
  };

  const footerPortalContent = useMemo(() => {
    return createPortal(
      <HStack className="fixed bottom-0 h-16 w-full border-t bg-white">
        <AppointmentAudio appointmentId={appointmentId} tenantId={tenantId} />
      </HStack>,
      document.getElementById('portal-footer') || document.body,
    );
  }, [appointmentId, tenantId]);

  if (!llmSummary) {
    return (
      <VStack>
        <P>No summary available. Cannot create golden summary.</P>
      </VStack>
    );
  }

  return (
    <VStack className="mt-4">
      <HStack gap={4} className="items-start">
        <Box className="min-w-0 flex-1 rounded-lg border border-border-primary p-4">
          <VStack gap={2}>
            <HStack className="justify-between rounded-xl bg-bg-quaternary p-2">
              <NewButton
                onClick={() => {
                  if (activeTab === 'TRANSCRIPT') return;
                  setActiveTab('TRANSCRIPT');
                }}
                type={`${activeTab === 'TRANSCRIPT' ? 'primary' : 'outline'}`}
                wFull
                text="Transcript"
              />
              {correctedTranscription && (
                <NewButton
                  onClick={() => {
                    if (activeTab === 'CORRECTED_TRANSCRIPT') return;
                    setActiveTab('CORRECTED_TRANSCRIPT');
                  }}
                  type={`${
                    activeTab === 'CORRECTED_TRANSCRIPT' ? 'primary' : 'outline'
                  }`}
                  wFull
                  text="Corrected Transcript"
                />
              )}
              <NewButton
                onClick={() => {
                  if (activeTab === 'SUMMARY') return;
                  setActiveTab('SUMMARY');
                }}
                type={`${activeTab === 'SUMMARY' ? 'primary' : 'outline'}`}
                wFull
                text="Original Summary"
              />
            </HStack>
            <VStack className="mt-1 max-h-[80svh] overflow-y-scroll pr-4 scrollbar-thin scrollbar-track-bg-quaternary scrollbar-thumb-black scrollbar-track-rounded-full scrollbar-thumb-rounded-full">
              {match(activeTab)
                .with('TRANSCRIPT', () => (
                  <VStack gap={4}>
                    {transcriptSegments.map((segment, index) => (
                      <Fragment key={index}>
                        <TranscriptTimestampDivider
                          timestamp={segment.recordingStartedAt}
                          action="started"
                          dateFormat={undefined}
                        />
                        <P className="whitespace-pre-line leading-loose">
                          {segment.transcript}
                        </P>
                      </Fragment>
                    ))}
                  </VStack>
                ))
                .with('CORRECTED_TRANSCRIPT', () => (
                  <P className="whitespace-pre-line leading-loose">
                    {correctedTranscription}
                  </P>
                ))
                .otherwise(() => {
                  return (
                    <DynamicArtifactRenderer
                      isReadonly={true}
                      blocks={llmSummary.blocks}
                    />
                  );
                })}
            </VStack>
          </VStack>
        </Box>

        <Box className="min-w-0 flex-1 rounded-lg border border-border-primary p-4">
          <VStack gap={2}>
            <GoldenSummaryContextProvider>
              <GoldenSummarySelect
                onSelectSummary={handleNoteChange}
                originalContent={originalContent}
              />
              {!activeNoteId ? (
                <VStack
                  gap={4}
                  className="h-full items-center justify-center py-8"
                >
                  <P>Select an existing golden summary or create a new one.</P>
                </VStack>
              ) : (
                <VStack className="my-1 max-h-[80svh] overflow-y-scroll pr-4 scrollbar-thin scrollbar-track-bg-quaternary scrollbar-thumb-black scrollbar-track-rounded-full scrollbar-thumb-rounded-full">
                  <GoldenSummaryEditor
                    key={activeNoteId}
                    tenantId={tenantId}
                    appointmentId={appointmentId}
                    activeNoteId={activeNoteId}
                  />
                </VStack>
              )}
            </GoldenSummaryContextProvider>
          </VStack>
        </Box>
      </HStack>
      {footerPortalContent}
    </VStack>
  );
};
